import {
  setLoading,
  setLoaded,
  setError,
  clearError as clearOpError,
  isOperationLoading,
  hasOperationLoaded,
} from '@gw/ui';

import { ApiError } from '@/api/apiError';
import { datasetsApi } from '@/api/datasets';
import { orgApiKeysApi } from '@/api/orgApiKeys';
import { createAsyncSliceAction } from '@/stores/helpers/asyncSliceAction';

import type {
  Dataset,
  CreateDatasetRequest,
  UpdateDatasetRequest,
  ListDatasetsParams,
  ListDatasetsResponse,
  DatasetStatus,
  QueryDatasetRequest,
  QueryDatasetResponse,
  OrgApiKeyInfo,
  GenerateOrgApiKeyResponse,
  LoadingStates,
  LoadedTimestamps,
  ErrorStates,
  QueryExportResponse,
} from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// Server-supported params for a dataset fetch. Forwards params the
// gw-datasets-api GET /v1/datasets endpoint honors (limit, search, status).
// GW-8565: `owned` opts into the server-side org-owned listing mode
// (GET /v1/datasets?owned=true). Owned mode is scoped server-side to the
// caller's organization and supports real cursor pagination, so it writes to
// its own state (ownedDatasets/ownedCursor/ownedHasMore) under the 'listOwned'
// operation key — never aliasing the public 'list' path.
export interface FetchDatasetsParams {
  search?: string;
  status?: DatasetStatus;
  limit?: number;
  owned?: boolean;
}

export interface DatasetsSlice {
  // State properties
  datasets: Dataset[];
  // GW-8565: Owned-mode listing state (server-side org-scoped, cursor-paginated).
  // Kept fully separate from the public `datasets` array so owned and public
  // listings can never alias in either navigation order.
  ownedDatasets: Dataset[];
  ownedCursor?: string;
  ownedHasMore: boolean;
  currentDataset: Dataset | null;
  isLoading: LoadingStates; // Object with keys: fetch, select, create, update, delete, fetchById
  hasLoaded: LoadingStates; // Object tracking which operations have completed
  lastLoaded: LoadedTimestamps; // Object tracking timestamps of completed operations
  errors: ErrorStates; // Object tracking errors per operation

  // GW-1246: Query/Export state
  queryResults: QueryDatasetResponse | null;
  queryLoading: boolean;
  queryError: string | null;
  exportLoading: boolean;

  // GW-1779: Org-level API key state
  orgApiKey: OrgApiKeyInfo | null;

  // Action methods
  fetchDatasets: (params?: FetchDatasetsParams) => Promise<void>;
  // GW-8565: Fetch the next owned-mode page via ownedCursor and append to
  // ownedDatasets. No-op once ownedHasMore is false.
  loadMoreDatasets: (params?: { limit?: number }) => Promise<void>;
  fetchDatasetById: (datasetId: string) => Promise<void>; // GW-1235: Fetch single dataset from API
  fetchDatasetBySlug: (slug: string) => Promise<void>; // GW-13274: Fetch single dataset by slug from API
  selectDataset: (datasetId: string) => Promise<void>;
  // GW-11230: returns the created Dataset so callers can perform a post-create
  // icon upload (create-mode has no datasetId until the record exists).
  createDataset: (dataset: CreateDatasetRequest) => Promise<Dataset>;
  updateDataset: (datasetId: string, updates: UpdateDatasetRequest) => Promise<void>;
  deleteDataset: (datasetId: string) => Promise<void>;
  clearCurrentDataset: () => void; // GW-1235: Clear current dataset when navigating away
  clearError: (operation?: string) => void; // Clear error for specific operation or all errors

  // GW-1246: Query/Export actions
  queryDataset: (datasetId: string, params: QueryDatasetRequest) => Promise<void>;
  clearQueryResults: () => void;
  exportQuery: (queryId: string, format?: 'json' | 'csv') => Promise<QueryExportResponse>;

  // GW-1779: Org-level API key actions
  fetchOrgApiKey: (orgId: string) => Promise<void>;
  generateOrgApiKey: (orgId: string) => Promise<GenerateOrgApiKeyResponse>;
  revokeOrgApiKey: (orgId: string, keyId: string) => Promise<void>;
  regenerateOrgApiKey: (orgId: string) => Promise<GenerateOrgApiKeyResponse>;

  // GW-2008: Reveal secret actions
  revealOrgApiKey: (
    orgId: string
  ) => Promise<{ keyId: string; keyValue: string; keyPrefix: string }>;
}

const initialDatasetsState = {
  datasets: [],
  // GW-8565: owned-mode listing state
  ownedDatasets: [],
  ownedCursor: undefined,
  ownedHasMore: false,
  currentDataset: null,
  isLoading: {},
  hasLoaded: {},
  lastLoaded: {},
  errors: {},
  // GW-1246: Query/Export state
  queryResults: null,
  queryLoading: false,
  queryError: null,
  exportLoading: false,
  // GW-1779: Org-level API key state
  orgApiKey: null,
};

export const createDatasetsSlice: StateCreator<DatasetsSlice, [], [], DatasetsSlice> = (
  set,
  get
) => ({
  ...initialDatasetsState,

  fetchDatasets: async (params?: FetchDatasetsParams) => {
    const currentState = get();
    const owned = params?.owned === true;

    // GW-8565: owned mode uses the 'listOwned' operation key + ownedDatasets
    // state; public mode keeps the byte-identical 'list' → datasets wiring so
    // the marketplace path is untouched.
    const operation = owned ? 'listOwned' : 'list';

    // Guard: Don't fetch if already loading or if we've already fetched successfully
    if (isOperationLoading(currentState.isLoading, operation)) {
      return;
    }
    if (hasOperationLoaded(currentState.hasLoaded, operation)) {
      return;
    }

    await createAsyncSliceAction<DatasetsSlice, ListDatasetsResponse>(
      operation,
      () => {
        const listParams: ListDatasetsParams = {
          limit: params?.limit ?? 100,
          ...(owned ? { owned: true } : {}),
          ...(params?.search ? { search: params.search } : {}),
          ...(params?.status ? { status: params.status } : {}),
        };
        return datasetsApi.listDatasets(listParams);
      },
      (page) => {
        // GW-7010: filter out draft datasets from the default list view
        const visible = page.datasets.filter((d) => d.status !== 'draft');
        if (owned) {
          return {
            ownedDatasets: visible,
            ownedCursor: page.pagination?.cursor,
            ownedHasMore: page.pagination?.hasMore ?? false,
          };
        }
        return { datasets: visible };
      },
      set,
      get
    )();
  },

  // GW-8565: Owned-mode cursor pagination. Fetches ONE next page via
  // ownedCursor and APPENDS it to ownedDatasets. No-op when there is no more
  // to load, no cursor, or an owned fetch is already in flight — no drain loop.
  loadMoreDatasets: async (params?: { limit?: number }) => {
    const currentState = get();

    if (!currentState.ownedHasMore || !currentState.ownedCursor) {
      return;
    }
    if (isOperationLoading(currentState.isLoading, 'listOwned')) {
      return;
    }

    const cursor = currentState.ownedCursor;

    await createAsyncSliceAction<DatasetsSlice, ListDatasetsResponse>(
      'listOwned',
      () =>
        datasetsApi.listDatasets({
          owned: true,
          cursor,
          limit: params?.limit ?? 100,
        }),
      (page) => {
        const existing = get().ownedDatasets;
        // GW-7010: filter out draft datasets from the appended page
        const visible = page.datasets.filter((d) => d.status !== 'draft');
        return {
          ownedDatasets: [...existing, ...visible],
          ownedCursor: page.pagination?.cursor,
          ownedHasMore: page.pagination?.hasMore ?? false,
        };
      },
      set,
      get
    )();
  },

  // GW-1235: Fetch a single dataset by ID from the API
  fetchDatasetById: async (datasetId: string) => {
    const currentState = get();

    // Guard: Don't fetch if already loading this dataset
    if (isOperationLoading(currentState.isLoading, 'fetchById')) {
      return;
    }

    await createAsyncSliceAction<DatasetsSlice, Dataset>(
      'fetchById',
      () => datasetsApi.getDataset(datasetId),
      (dataset) => ({ currentDataset: dataset }),
      set,
      get
    )();
  },

  // GW-13274: Fetch a single dataset by its URL slug from the API. Uses the same
  // 'fetchById' operation key as fetchDatasetById so the DatasetDetailPage's
  // existing loading/error selectors (isLoading.fetchById / errors.fetchById)
  // cover both the UUID fast-path and the slug path with no extra state.
  fetchDatasetBySlug: async (slug: string) => {
    const currentState = get();

    // Guard: Don't fetch if already loading this dataset
    if (isOperationLoading(currentState.isLoading, 'fetchById')) {
      return;
    }

    await createAsyncSliceAction<DatasetsSlice, Dataset>(
      'fetchById',
      () => datasetsApi.getDatasetBySlug(slug),
      (dataset) => ({ currentDataset: dataset }),
      set,
      get
    )();
  },

  selectDataset: async (datasetId: string) => {
    const currentState = get();
    set({
      isLoading: setLoading(currentState.isLoading, 'select', true),
      errors: clearOpError(currentState.errors, 'select'),
    });

    try {
      const dataset = get().datasets.find((d) => d.id === datasetId);
      if (dataset) {
        const state = get();
        const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'select', true);

        set({
          currentDataset: dataset,
          isLoading: setLoading(state.isLoading, 'select', false),
          hasLoaded: loadedState.hasLoaded,
          lastLoaded: loadedState.lastLoaded,
        });
        localStorage.setItem('gw-current-dataset-id', datasetId);
      } else {
        throw new Error('Dataset not found');
      }
    } catch (error) {
      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to select dataset';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'select', false);

      set({
        isLoading: setLoading(state.isLoading, 'select', false),
        errors: setError(state.errors, 'select', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  createDataset: async (dataset: CreateDatasetRequest) => {
    const currentState = get();
    set({
      isLoading: setLoading(currentState.isLoading, 'create', true),
      errors: clearOpError(currentState.errors, 'create'),
    });

    try {
      const newDataset = await datasetsApi.createDataset(dataset);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'create', true);

      set({
        datasets: [...state.datasets, newDataset],
        // GW-13584: Keep ownedDatasets in sync with the newly created dataset so
        // the publisher's owned listing immediately reflects it without a refetch.
        ownedDatasets: [...state.ownedDatasets, newDataset],
        isLoading: setLoading(state.isLoading, 'create', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });

      return newDataset;
    } catch (error) {
      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to create dataset';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'create', false);

      set({
        isLoading: setLoading(state.isLoading, 'create', false),
        errors: setError(state.errors, 'create', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  updateDataset: async (datasetId: string, updates: UpdateDatasetRequest) => {
    const currentState = get();
    set({
      isLoading: setLoading(currentState.isLoading, 'update', true),
      errors: clearOpError(currentState.errors, 'update'),
    });

    try {
      const updatedDataset = await datasetsApi.updateDataset(datasetId, updates);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'update', true);

      set({
        datasets: state.datasets.map((d) => (d.id === datasetId ? updatedDataset : d)),
        ownedDatasets: state.ownedDatasets.map((d) => (d.id === datasetId ? updatedDataset : d)),
        currentDataset:
          state.currentDataset?.id === datasetId ? updatedDataset : state.currentDataset,
        isLoading: setLoading(state.isLoading, 'update', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to update dataset';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'update', false);

      set({
        isLoading: setLoading(state.isLoading, 'update', false),
        errors: setError(state.errors, 'update', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  deleteDataset: async (datasetId: string) => {
    const currentState = get();
    set({
      isLoading: setLoading(currentState.isLoading, 'delete', true),
      errors: clearOpError(currentState.errors, 'delete'),
    });

    try {
      await datasetsApi.deleteDataset(datasetId);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'delete', true);

      set({
        datasets: state.datasets.filter((d) => d.id !== datasetId),
        currentDataset: state.currentDataset?.id === datasetId ? null : state.currentDataset,
        isLoading: setLoading(state.isLoading, 'delete', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete dataset';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'delete', false);

      set({
        isLoading: setLoading(state.isLoading, 'delete', false),
        errors: setError(state.errors, 'delete', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  // GW-1235: Clear current dataset when navigating away from detail page
  clearCurrentDataset: () => {
    set({ currentDataset: null });
  },

  clearError: (operation?: string) => {
    const currentState = get();
    if (operation) {
      set({ errors: clearOpError(currentState.errors, operation) });
    } else {
      set({ errors: {} });
    }
  },

  // GW-1246: Query dataset action
  queryDataset: async (datasetId: string, params: QueryDatasetRequest) => {
    set({
      queryLoading: true,
      queryError: null,
    });

    try {
      const results = await datasetsApi.queryDataset(datasetId, params);
      set({
        queryResults: results,
        queryLoading: false,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to execute query';
      set({
        queryResults: null,
        queryError: errorMessage,
        queryLoading: false,
      });
      throw error;
    }
  },

  // GW-1246: Clear query results action
  clearQueryResults: () => {
    set({
      queryResults: null,
      queryError: null,
    });
  },

  // GW-1949: Export query results via presigned URL
  exportQuery: async (queryId: string, format: 'json' | 'csv' = 'json') => {
    set({ exportLoading: true });

    try {
      const result = await datasetsApi.exportQuery(queryId, format);
      set({ exportLoading: false });
      return result;
    } catch (error) {
      set({ exportLoading: false });
      throw error;
    }
  },

  // GW-1779: Fetch org-level API key metadata
  fetchOrgApiKey: async (orgId: string) => {
    const currentState = get();

    // Guard: Don't fetch if already loading
    if (isOperationLoading(currentState.isLoading, 'fetchOrgApiKey')) {
      return;
    }

    await createAsyncSliceAction<DatasetsSlice, OrgApiKeyInfo | null>(
      'fetchOrgApiKey',
      async () => {
        try {
          return await orgApiKeysApi.getOrgApiKey(orgId);
        } catch (error) {
          // 404 means the org has no API key yet — expected state, not an error
          if (error instanceof ApiError && error.status === 404) {
            return null;
          }
          throw error;
        }
      },
      (orgApiKey) => ({ orgApiKey }),
      set,
      get
    )();
  },

  // GW-1779: Generate org-level API key (returns response to component)
  generateOrgApiKey: async (orgId: string) => {
    const result = await orgApiKeysApi.generateOrgApiKey(orgId);
    set({
      orgApiKey: {
        keyId: result.keyId,
        keyPrefix: result.keyPrefix,
        createdAt: result.createdAt,
      },
    });
    return result;
  },

  // GW-1779: Revoke org-level API key
  revokeOrgApiKey: async (orgId: string, keyId: string) => {
    await orgApiKeysApi.revokeOrgApiKey(orgId, keyId);
    set({ orgApiKey: null });
  },

  // GW-1779: Regenerate org-level API key (returns response to component)
  regenerateOrgApiKey: async (orgId: string) => {
    const result = await orgApiKeysApi.regenerateOrgApiKey(orgId);
    set({
      orgApiKey: {
        keyId: result.keyId,
        keyPrefix: result.keyPrefix,
        createdAt: result.createdAt,
      },
    });
    return result;
  },

  // GW-2008: Reveal org-level API key (returns full key value)
  revealOrgApiKey: async (orgId: string) => {
    return orgApiKeysApi.revealOrgApiKey(orgId);
  },
});
